import type { Recebimento } from '@/api/robo'

/**
 * Os seis passos do wizard de recebimento (spec 2026-09-27, 4.4), na ordem.
 *
 * Três são OBRIGATÓRIOS e vêm da API (`recebimento.pendencias`): recebimento,
 * nome e escopo. Os outros três são opcionais — "Faço isso depois" na rede, e
 * alertas/aparência têm valor padrão — e só são lembrados como VISTOS, em
 * `sessionStorage`, para quem reabre a página não ser cobrado de novo.
 */
export const PASSOS = ['recebimento', 'rede', 'nome', 'escopo', 'alertas', 'aparencia'] as const
export type Passo = typeof PASSOS[number]

const OBRIGATORIOS = new Set<Passo>(['recebimento', 'nome', 'escopo'])

/** O primeiro passo a mostrar: obrigatório pendente, ou opcional ainda não visto. */
export function primeiroPendente(rec: Recebimento, visitados: Set<Passo>): Passo {
  const pendentes = new Set<string>(rec.pendencias)
  for (const passo of PASSOS) {
    if (OBRIGATORIOS.has(passo)) {
      if (pendentes.has(passo)) return passo
    } else if (!visitados.has(passo)) {
      return passo
    }
  }
  return 'aparencia'
}

/**
 * Wizard ou tela única. Com pendência obrigatória, sempre o wizard. Sem ela, o
 * wizard só continua se já estava em curso nesta sessão: concluir o escopo não
 * pode expulsar quem ainda está olhando os alertas.
 */
export function emWizard(rec: Recebimento | undefined, emCursoNestaSessao: boolean): boolean {
  if (!rec) return false
  return rec.pendencias.length > 0 || emCursoNestaSessao
}

const chave = (publicId: string) => `robo-recebimento:${publicId}`

/** Os passos opcionais já vistos. Storage bloqueado ou lixo viram conjunto vazio. */
export function lerVisitados(publicId: string, storage: Storage | undefined = globalThis.sessionStorage): Set<Passo> {
  try {
    const bruto = JSON.parse(storage?.getItem(chave(publicId)) ?? '[]')
    if (!Array.isArray(bruto)) return new Set()
    return new Set(bruto.filter((p): p is Passo => (PASSOS as readonly unknown[]).includes(p)))
  } catch {
    return new Set()
  }
}

export function gravarVisitados(publicId: string, visitados: Set<Passo>,
  storage: Storage | undefined = globalThis.sessionStorage): void {
  try {
    storage?.setItem(chave(publicId), JSON.stringify([...visitados]))
  } catch {
    // Sem storage o wizard recomeça no primeiro obrigatório pendente: aceitável.
  }
}
